/* ==========================================================================
   ZAITUNY  -  motion controller
   --------------------------------------------------------------------------
   Three jobs, nothing more:

     1. orchestrate the hero load-in (frame stitches, then content settles)
     2. reveal sections on scroll, staggered, once each
     3. measure each stitched band so its steps() count is one real stitch

   Constraints this file holds to:
     - no scroll listeners and no rAF loops; IntersectionObserver only
     - no animation of anything but transform, opacity, and clip-path
     - it never hides anything it cannot later show
     - it re-arms itself when the theme editor swaps a section in

   The `.zt-motion` class is added by an inline script in theme.liquid before
   first paint, and only when the visitor has not asked for reduced motion.
   If it is absent this file exits immediately and the page is simply static.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('zt-motion')) return;
  if (!('IntersectionObserver' in window)) {
    root.classList.remove('zt-motion');
    return;
  }

  var STITCH_PX = 8; /* must match --zt-stitch */
  var STAGGER_MS = 70;
  var MAX_STAGGER_STEPS = 6; /* past this the cascade reads as a delay, not a rhythm */

  /* ---------------------------------------------------------- utilities -- */

  /* A stepped clip only reads as embroidery if each step is one stitch wide.
     Measure the band and hand the real count to the transition. */
  function applyStitchTiming(el) {
    var vertical = el.dataset.ztStitchFrom === 'top' || el.dataset.ztStitchFrom === 'bottom';
    var rect = el.getBoundingClientRect();
    var length = vertical ? rect.height : rect.width;
    if (!length) return;

    var steps = Math.max(6, Math.min(160, Math.round(length / STITCH_PX)));
    var duration = Math.max(420, Math.min(1100, steps * 9));

    el.style.transitionProperty = 'clip-path';
    el.style.transitionDuration = duration + 'ms';
    el.style.transitionTimingFunction = 'steps(' + steps + ', end)';
  }

  function stitchIn(el, delay) {
    applyStitchTiming(el);
    el.style.setProperty('--zt-stitch-delay', (delay || 0) + 'ms');
    /* Read back before flipping state so the pending clip is committed and the
       transition actually runs rather than being collapsed into one frame. */
    void el.offsetWidth;
    el.dataset.ztStitchState = 'in';
  }

  function revealIn(el, delay) {
    el.style.setProperty('--zt-reveal-delay', (delay || 0) + 'ms');
    el.dataset.ztReveal = 'in';
  }

  /* Children of a reveal group share one cascade. Delay is capped so a long
     list does not leave its last item waiting. */
  function revealGroup(container) {
    var items = container.matches('.zt-reveal')
      ? [container]
      : Array.prototype.slice.call(container.querySelectorAll('.zt-reveal'));

    items.forEach(function (item, i) {
      revealIn(item, Math.min(i, MAX_STAGGER_STEPS) * STAGGER_MS);
    });

    Array.prototype.slice.call(container.querySelectorAll('[data-zt-stitch]')).forEach(function (band, i) {
      stitchIn(band, Math.min(i, MAX_STAGGER_STEPS) * STAGGER_MS);
    });
  }

  /* ------------------------------------------------------ scroll reveals -- */

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        revealGroup(entry.target);
        observer.unobserve(entry.target);
      });
    },
    {
      /* Fire a little before the section reaches the fold so the motion is
         already settling by the time the reader looks at it. */
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.08,
    }
  );

  function observeAll(scope) {
    Array.prototype.slice
      .call((scope || document).querySelectorAll('[data-zt-reveal-group]'))
      .forEach(function (el) {
        if (el.dataset.ztObserved === 'true') return;
        el.dataset.ztObserved = 'true';
        observer.observe(el);
      });
  }

  /* --------------------------------------------------------- hero load-in -- */

  /* The opening sequence. The four frame edges are worked from their four
     corners at once so the border closes in on the content like paper folding
     round the cube, and the content begins settling before the frame finishes
     so the whole thing lands in about a second rather than feeling sequential.

       0ms    frame edges start, all four together
       380ms  content begins, staggered by 70ms
   */
  function runHero(hero) {
    if (hero.dataset.ztHeroRun === 'true') return;
    hero.dataset.ztHeroRun = 'true';

    Array.prototype.slice.call(hero.querySelectorAll('[data-zt-stitch]')).forEach(function (edge) {
      stitchIn(edge, 0);
    });

    var items = Array.prototype.slice.call(hero.querySelectorAll('.zt-reveal'));
    items.forEach(function (item, i) {
      revealIn(item, 380 + Math.min(i, MAX_STAGGER_STEPS) * STAGGER_MS);
    });
  }

  function startHero(scope) {
    var hero = (scope || document).querySelector('[data-zt-hero]');
    if (!hero) return;

    /* Wait for the display face before running the sequence, so the headline
       does not reflow mid-animation. Capped, so a slow font never blocks it. */
    var start = function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          runHero(hero);
        });
      });
    };

    if (document.fonts && document.fonts.ready) {
      var settled = false;
      var go = function () {
        if (settled) return;
        settled = true;
        start();
      };
      document.fonts.ready.then(go);
      setTimeout(go, 700);
    } else {
      start();
    }
  }

  /* ------------------------------------------------------------- wire up -- */

  function init(scope) {
    observeAll(scope);
    startHero(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* The theme editor replaces a section's markup wholesale. Re-arm inside the
     new subtree only, and run its hero sequence immediately so the merchant
     sees the animation they are editing. */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    init(section);
    var hero = section.querySelector('[data-zt-hero]');
    if (hero) runHero(hero);
    var group = section.querySelector('[data-zt-reveal-group]');
    if (group) revealGroup(group);
  });
})();

/* ==========================================================================
   Behaviour
   --------------------------------------------------------------------------
   Deliberately outside the motion guard above. Everything here is behaviour,
   not animation, so it must run for visitors who have asked for reduced
   motion. Under that preference the transitions collapse to 1ms in
   zaituny.css, so the states still change, they just do not animate.

   1. Menu drawer. It is a native <details>, which already opens, closes and
      answers the keyboard on its own. This adds only the two things <details>
      does not give you for free: escape closes it, and a click outside
      dismisses it, with focus returned to the toggle either way.

   2. Add to cart feedback. The button acknowledges the press immediately and
      holds the confirmation long enough to read, then returns. Enter is
      deliberate, exit is quick. Someone who cannot see motion still needs to
      know the press registered, which is exactly why this is not up above.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------- drawer -- */

  function closeDrawer(drawer, returnFocus) {
    if (!drawer.open) return;
    drawer.open = false;
    if (returnFocus) {
      var toggle = drawer.querySelector('summary');
      if (toggle) toggle.focus();
    }
  }

  function openDrawers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-zt-drawer][open]'));
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    openDrawers().forEach(function (drawer) {
      closeDrawer(drawer, true);
    });
  });

  document.addEventListener('click', function (event) {
    openDrawers().forEach(function (drawer) {
      if (!drawer.contains(event.target)) closeDrawer(drawer, false);
    });
  });

  /* ------------------------------------------------- add to cart feedback -- */

  function bindCartFeedback(scope) {
    Array.prototype.slice
      .call((scope || document).querySelectorAll('[data-zt-add]'))
      .forEach(function (btn) {
        if (btn.dataset.ztBound === 'true') return;
        btn.dataset.ztBound = 'true';

        var form = btn.closest('form');
        if (!form) return;

        form.addEventListener('submit', function () {
          if (btn.dataset.ztState === 'working') return;
          btn.dataset.ztState = 'working';
          window.setTimeout(function () {
            btn.dataset.ztState = 'done';
            window.setTimeout(function () {
              btn.removeAttribute('data-zt-state');
            }, 1600);
          }, 320);
        });
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindCartFeedback(document);
    });
  } else {
    bindCartFeedback(document);
  }

  /* The theme editor swaps section markup wholesale, so re-bind inside the
     new subtree. */
  document.addEventListener('shopify:section:load', function (event) {
    bindCartFeedback(event.target);
  });
})();
